/**
 * 코치 패널 접기/펼치기 상태 — 사용자별 저장 + 좁은 화면 기본 접힘.
 *
 * 좁은 화면에서 기본을 접힘으로 두는 이유: 코치는 **부가 정보**이고 자막이
 * 본론이다(§14). 좁은 폭에서 패널이 펼쳐져 있으면 자막 칼럼이 읽기 어려운
 * 폭까지 눌린다. 넓은 화면에서는 둘 다 들어가므로 펼쳐 둔다.
 *
 * 저장은 **사용자가 직접 접거나 편 경우에만** 한다. 창 크기 때문에 자동으로
 * 접힌 것을 취향으로 기억하면, 창을 넓혔을 때도 계속 접혀 있게 된다.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'omnitrans:coach-collapsed'

/**
 * 이 폭 미만에서는 패널이 자막을 밀어내지 않고 **덮는다**(overlay).
 * lg(1024px) 는 host 레이아웃이 우측 패널을 붙이기 시작하는 폭이다.
 */
export const COACH_OVERLAY_MAX_WIDTH = 1024

function readStored(): boolean | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === null ? null : raw === '1'
  } catch {
    return null
  }
}

function isNarrow(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth < COACH_OVERLAY_MAX_WIDTH
}

export function useCoachCollapse(): {
  collapsed: boolean
  /** 좁은 화면 — 패널이 피드를 밀지 않고 덮는다. */
  narrow: boolean
  toggle: () => void
  setCollapsed: (collapsed: boolean) => void
} {
  const [narrow, setNarrow] = useState(isNarrow)
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    const stored = readStored()
    // 저장된 취향이 있으면 그것을, 없으면 폭으로 정한다.
    return stored ?? isNarrow()
  })
  /** 사용자가 직접 정한 적이 있는지 — 창 크기 변화가 취향을 덮지 않게. */
  const explicitRef = useRef(readStored() !== null)

  useEffect(() => {
    const onResize = (): void => {
      const nowNarrow = isNarrow()
      setNarrow(nowNarrow)
      // 취향을 밝힌 적이 없을 때만 폭을 따른다.
      if (!explicitRef.current) setCollapsedState(nowNarrow)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const setCollapsed = useCallback((next: boolean) => {
    explicitRef.current = true
    setCollapsedState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
    } catch {
      // 저장 실패는 무시한다 — 이번 세션만 유지된다.
    }
  }, [])

  const toggle = useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  return { collapsed, narrow, toggle, setCollapsed }
}
